import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Hls from 'hls.js';
import { useSearchParams } from 'react-router-dom';
import { useNode } from '../contexts/NodeContext';
import {
  getNodeCameras,
  getNodeGroups,
  getRecordingDates,
  getRecordingSegments,
  getRecordingTriggers,
  getRecordingConfig,
  getRecordingConfigs,
  getRecordingStorageByCamera,
} from '../services/api.service';
import {
  VideoCameraIcon,
  ArrowLeftIcon,
  ClockIcon,
  CalendarDaysIcon,
  FilmIcon,
  ArrowDownTrayIcon,
  ScissorsIcon,
  FolderIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { RecordingTimeline, RecordingCameraCard, PlaybackTransport, recordingModeBadge, isCameraOnline, IMPLICIT_RECORDING_CONFIG } from '@trinetra/shared';

const API_BASE = window.location.origin + '/api';

/* ─── helpers ─── */
const fmtSize = (b: number) => {
  if (!b) return '—';
  if (b > 1e9) return (b / 1e9).toFixed(1) + ' GB';
  if (b > 1e6) return (b / 1e6).toFixed(1) + ' MB';
  return (b / 1e3).toFixed(0) + ' KB';
};

const fmtDuration = (s: number) => {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};
const fmtDurationFromTimes = (start: string, end: string) => {
  try {
    const ms = new Date(end).getTime() - new Date(start).getTime();
    return fmtDuration(ms / 1000);
  } catch { return '—'; }
};
const fmtTime = (iso: string | Date) => {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
const fmtDate = (dateStr: string) => {
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d); // local midnight
    return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return dateStr; }
};

// "HH:mm" (24h) ↔ 12h picker parts
const parseHM = (v: string) => {
  const [H, M] = (v || '00:00').split(':').map(Number);
  return { h12: (H % 12) || 12, min: M || 0, pm: H >= 12 };
};
const buildHM = (h12: number, min: number, pm: boolean) =>
  `${String((h12 % 12) + (pm ? 12 : 0)).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
const fmt12 = (v: string) => {
  const { h12, min, pm } = parseHM(v);
  return `${h12}:${String(min).padStart(2, '0')} ${pm ? 'PM' : 'AM'}`;
};

const SPEEDS = [0.5, 1, 2, 4, 8, 16];

// A stale in-progress row (recorder died before finalizing) must not create a
// phantom live tail — mirrors the local backend's LIVE_TAIL_LOOKBACK_MS.
const LIVE_TAIL_MAX_AGE_MS = 30 * 60 * 1000;

type PlaybackState = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

/**
 * Nest a flat group list (rows with parent_id, e.g. from the getTree CTE) into a
 * parent→children tree. Returns the list unchanged if it is already nested.
 */
function buildGroupTree(flat: any[]): any[] {
  if (!Array.isArray(flat) || flat.length === 0) return [];
  if (flat[0]?.children) return flat;
  const map: Record<string, any> = {};
  const roots: any[] = [];
  for (const g of flat) map[g.id] = { ...g, children: [] };
  for (const g of flat) {
    if (g.parent_id && map[g.parent_id]) map[g.parent_id].children.push(map[g.id]);
    else roots.push(map[g.id]);
  }
  return roots;
}

/** Recursive group-tree renderer — same UI as the Cameras page group filter. */
function renderGroupTree(groups: any[], selectedId: string | undefined, onSelect: (g: any) => void, level = 0) {
  return groups.map((g: any) => (
    <div key={g.id}>
      <button
        onClick={() => onSelect(g)}
        className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] transition-colors ${selectedId === g.id ? 'bg-brand/10 font-semibold text-brand' : 'hover:bg-muted text-foreground'
          }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
      >
        <FolderIcon className="h-4 w-4 text-yellow-400 flex-shrink-0" />
        <span className="truncate">{g.name}</span>
      </button>
      {g.children && renderGroupTree(g.children, selectedId, onSelect, level + 1)}
    </div>
  ));
}

/** Find a group by id within a (possibly nested) group tree. */
function findGroupById(groups: any[], id: string): any {
  for (const g of groups) {
    if (g.id === id) return g;
    const found = g.children && findGroupById(g.children, id);
    if (found) return found;
  }
  return null;
}

export default function RecordingsPage() {
  const { selectedNode } = useNode();
  const [searchParams] = useSearchParams();
  const [cameras, setCameras] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  // camera_id → recording config, for the picker's per-camera mode badge. null until the
  // one bulk request lands, which is what makes the badge say "Checking…" rather than
  // asserting a mode it hasn't been told yet. `recConfigsFailed` is tracked separately
  // because "the request failed" must NOT collapse into "no row, so continuous" — that
  // would label every camera with a mode nobody confirmed, and over the tunnel this call
  // fails for reasons that have nothing to do with the cameras.
  const [recConfigs, setRecConfigs] = useState<Record<string, any> | null>(null);
  const [recConfigsFailed, setRecConfigsFailed] = useState(false);
  /** camera_id → { segment_count, total_size, oldest_segment, newest_segment }. */
  const [storageByCamera, setStorageByCamera] = useState<Record<string, any>>({});
  // Tracked separately from an empty map: over a tunnel this call fails for reasons that have
  // nothing to do with the cameras, and "we couldn't read it" must not render as "nothing
  // recorded".
  const [storageFailed, setStorageFailed] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [cameraPage, setCameraPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const CAMERAS_PER_PAGE = 16;

  const [selectedCamera, setSelectedCamera] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [segments, setSegments] = useState<any[]>([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  // Trigger markers + the camera's mode: timeline presentation only. On a triggered camera the
  // gaps are expected, and an unlabelled bar full of holes reads as a fault.
  const [recTriggers, setRecTriggers] = useState<any[]>([]);
  const [recMode, setRecMode] = useState<string | null>(null);
  const [rangeStart, setRangeStart] = useState('00:00');
  const [rangeEnd, setRangeEnd] = useState('23:59');
  const [pendingStart, setPendingStart] = useState('00:00');
  const [pendingEnd, setPendingEnd] = useState('23:59');
  const isRangeValid = pendingStart < pendingEnd;

  // Unified playback: ONE seekable range VOD playlist played through hls.js.
  // Seeking is just a currentTime change on this single source — no per-segment reload.
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [activeSegId, setActiveSegId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const playbackRateRef = useRef(1);
  const [currentTime, setCurrentTime] = useState<Date | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const filteredSegmentsRef = useRef<any[]>([]);
  const playbackSegsRef = useRef<any[]>([]);   // finalized segments with player-time offsets
  const playbackUrlRef = useRef<string | null>(null);
  const rafRef = useRef<number | null>(null);
  const seekDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [playbackState, setPlaybackState] = useState<PlaybackState>('idle');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isBuffering, setIsBuffering] = useState(false);
  const bufferTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSeekRef = useRef<number | null>(null);  // player-time (s) once source loads
  const hlsRef = useRef<Hls | null>(null);
  const recoverRef = useRef({ media: 0, ts: 0 });
  // Repeated failures on a SINGLE fragment. A permanently gone chunk (segment
  // deleted by retention, or a transcode that gave up) would otherwise make us
  // startLoad() forever — an endless "Buffering…" with no error shown.
  const fragFailRef = useRef({ url: '', count: 0, ts: 0 });
  // Identifies this player to the node's playback service (?sid= on chunk
  // requests). It uses this to tell which in-flight transcodes a seek abandoned
  // and cancel them, instead of letting them hog the node's GPU ahead of the
  // segment we now want. Scrubbing over H265 recordings otherwise ends in a
  // permanent "Buffering…".
  const sessionIdRef = useRef(
    `c${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`,
  );
  // User's intended play/pause state. hls.js refires `canplay` at every playlist
  // discontinuity (each recording is a segment boundary) and after seeks — without
  // this guard those events would force-resume a video the user had paused.
  const wantPlayingRef = useRef(false);

  // Clip download state — pick a start/end on the timeline, then export that range.
  const RANGE_DOWNLOAD_MAX_MIN = 60; // mirrors local backend CLIP_EXPORT_MAX_SECONDS
  const [clipMode, setClipMode] = useState(false);
  const [clipSel, setClipSel] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [downloading, setDownloading] = useState(false);

  const { hasPermission } = useAuth();
  const canDownload = hasPermission('recordings.download');

  /* ═══════════════════════════════════════════
   * CORE: teardown + single-source playback
   * The whole range is one hls.js source (unified playlist through the
   * tunnel); seeking moves currentTime.
   * ═══════════════════════════════════════════ */

  const teardown = useCallback(() => {
    pendingSeekRef.current = null;
    if (seekDebounceRef.current) { clearTimeout(seekDebounceRef.current); seekDebounceRef.current = null; }
    const v = videoRef.current;
    if (v) v.pause();
    setPlaybackUrl(null);   // clearing the src tears down hls.js via the setup effect
    setIsPlaying(false);
    setActiveSegId(null);
    setLoadError(null);
  }, []);

  // player-time (s, contiguous across the playlist) ↔ wall-clock (Date), via the
  // finalized-segment offset table. Gaps in footage collapse in player-time, so a
  // seek into a gap snaps to the nearest segment.
  const wallToPlayer = useCallback((ms: number) => {
    const segs = playbackSegsRef.current;
    if (!segs.length) return 0;
    for (const p of segs) {
      if (ms >= p.wallStart && ms <= p.wallEnd) return p.playerStart + (ms - p.wallStart) / 1000;
    }
    let best = segs[0];
    let bestDist = Infinity;
    for (const p of segs) {
      const dist = ms < p.wallStart ? p.wallStart - ms : ms - p.wallEnd;
      if (dist < bestDist) { bestDist = dist; best = p; }
    }
    return ms > best.wallEnd ? Math.max(best.playerStart, best.playerEnd - 1) : best.playerStart;
  }, []);

  const playerToWall = useCallback((pt: number) => {
    const segs = playbackSegsRef.current;
    for (const p of segs) {
      if (pt >= p.playerStart && pt < p.playerEnd) return new Date(p.wallStart + (pt - p.playerStart) * 1000);
    }
    const last = segs[segs.length - 1];
    return last ? new Date(last.wallEnd) : null;
  }, []);

  // Start (or, if already loaded, seek within) playback at a given player-time.
  // Rapid jumps are debounced so a burst of seeks coalesces into the final position.
  const startPlayback = useCallback((playerTime = 0) => {
    if (!playbackSegsRef.current.length || !selectedNode || !selectedCamera) return;
    if (playbackUrlRef.current) {
      // Only flip to "playing" when the user actually intends to play — a seek/skip
      // issued while paused must move the playhead without auto-resuming.
      if (wantPlayingRef.current) { setIsPlaying(true); setPlaybackState('playing'); }
      if (seekDebounceRef.current) clearTimeout(seekDebounceRef.current);
      seekDebounceRef.current = setTimeout(() => {
        const v = videoRef.current;
        if (v) {
          try { v.currentTime = Math.max(0, playerTime); } catch { /* not seekable yet */ }
          if (wantPlayingRef.current) v.play().catch(() => { });
        }
      }, 120);
      return;
    }
    // Build the playlist URL fresh so it carries the current token. The window
    // MUST be the applied range bounds — the same filter playbackSegs mirrors —
    // or the backend could include an extra boundary segment and the
    // player-time ↔ wall-clock offset table would desync.
    const camId = selectedCamera.id || selectedCamera.camera_id;
    const token = localStorage.getItem('cloud_token') || '';
    const [y, mo, dy] = selectedDate.split('-').map(Number);
    const dayStart = new Date(y, mo - 1, dy, 0, 0, 0, 0);
    const [sh, sm] = rangeStart.split(':').map(Number);
    const [eh, em] = rangeEnd.split(':').map(Number);
    const start = new Date(dayStart.getTime() + (sh * 60 + (sm || 0)) * 60000).toISOString();
    const end = new Date(dayStart.getTime() + (eh * 60 + (em || 0)) * 60000).toISOString();
    const url = `${API_BASE}/nodes/${selectedNode.nodeId}/recordings/playlist/${camId}?start_time=${encodeURIComponent(start)}&end_time=${encodeURIComponent(end)}&token=${encodeURIComponent(token)}`;
    pendingSeekRef.current = playerTime > 0 ? playerTime : null;
    setPlaybackState('loading');
    setIsPlaying(true);
    setPlaybackUrl(url);
  }, [selectedNode, selectedCamera, selectedDate, rangeStart, rangeEnd]);

  // hls.js lifecycle bound to the single playback source.
  useEffect(() => {
    const video = videoRef.current;
    playbackUrlRef.current = playbackUrl;
    if (!playbackUrl || !video) return;

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        backBufferLength: 30,
        // Chunks transcode on demand on the node and travel through the WS
        // tunnel — generous timeouts/retries.
        manifestLoadingTimeOut: 60000,
        manifestLoadingMaxRetry: 4,
        manifestLoadingRetryDelay: 2000,
        levelLoadingTimeOut: 60000,
        fragLoadingTimeOut: 60000,
        fragLoadingMaxRetry: 6,
        fragLoadingRetryDelay: 500,
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 1,
        nudgeOffset: 0.2,
        nudgeMaxRetry: 10,
        appendErrorMaxRetry: 5,
        xhrSetup: (xhr, url) => {
          // Tag chunk requests with this player's session id. xhrSetup is the last
          // point the URL can be changed — opening here means hls.js keeps our
          // URL (it only opens the request itself if xhrSetup didn't).
          if (url.includes('/hls/') && url.includes('.ts')) {
            const sep = url.includes('?') ? '&' : '?';
            xhr.open('GET', `${url}${sep}sid=${sessionIdRef.current}`, true);
          }
        },
      });
      hlsRef.current = hls;
      hls.loadSource(playbackUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setPlaybackState('playing');
        setLoadError(null);
        if (pendingSeekRef.current != null) {
          try { video.currentTime = pendingSeekRef.current; } catch { /* not seekable yet */ }
          pendingSeekRef.current = null;
        }
        video.play().catch(() => { });
        video.playbackRate = playbackRateRef.current;
      });
      // A chunk that loads clears the failure streak, so only *sustained*
      // failures on one chunk (never any success between) count toward giving up.
      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        fragFailRef.current = { url: '', count: 0, ts: 0 };
      });
      const recover = recoverRef.current;
      hls.on(Hls.Events.ERROR, (_event: any, data: any) => {
        if (!data.fatal) {
          if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
            const v = videoRef.current;
            if (v) { try { v.currentTime += 0.1; } catch { /* ignore */ } }
          }
          return;
        }
        const status = data.response?.code || data.networkDetails?.status;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          if ((data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR
            || data.details === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT
            || data.details === Hls.ErrorDetails.MANIFEST_PARSING_ERROR)
            && (status === 404 || status === 410)) {
            hls.destroy();
            hlsRef.current = null;
            setPlaybackState('error');
            setLoadError('No recordings found for this range — they may have been removed by retention policy.');
            setIsPlaying(false);
            return;
          }
          // Fragment failure. Near the live edge or mid-transcode a 404/timeout
          // is genuinely transient, so keep resuming — but a chunk that never
          // recovers must not loop startLoad() forever, or the player freezes on
          // an endless spinner with no feedback. Count repeats on the SAME
          // fragment and surface a clear error once it's clearly permanent.
          const fragUrl = data.frag?.url || data.context?.url || '';
          const now = Date.now();
          const f = fragFailRef.current;
          if (fragUrl && fragUrl === f.url && now - f.ts < 30000) {
            f.count += 1;
          } else {
            f.url = fragUrl;
            f.count = 1;
          }
          f.ts = now;
          // 409 = the node cancelled that transcode because it believed this player
          // had seeked elsewhere. Normal while scrubbing, and it says nothing about
          // the recording, so it gets a long leash — but NOT the unlimited one it
          // used to get. An unbounded 409 retry is only ever correct if the node
          // eventually stops sending them, and when a cancel loop set in (each
          // request cancelling the other's transcode) this was what turned it into a
          // permanent silent "Buffering…". Scrubbing moves between fragments, so the
          // SAME fragment failing this many times in 30s is a stuck loop, not a user.
          const permanent = status === 404 || status === 410;
          if (f.count >= (permanent ? 6 : status === 409 ? 25 : 15)) {
            f.url = ''; f.count = 0;
            hls.destroy();
            hlsRef.current = null;
            setPlaybackState('error');
            setLoadError(permanent
              ? 'This part of the recording is unavailable — it may have been removed by the storage-retention policy, or the video failed to transcode.'
              : 'Playback stalled — this part of the recording could not be loaded.');
            setIsPlaying(false);
          } else {
            // Transient (tunnel hiccup, chunk still transcoding) — resume.
            try { hls.startLoad(); } catch { /* ignore */ }
          }
          return;
        }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          const now = Date.now();
          if (now - recover.ts > 5000) recover.media = 0;
          recover.ts = now;
          recover.media += 1;
          if (recover.media <= 1) {
            hls.recoverMediaError();
          } else if (recover.media === 2) {
            try { hls.swapAudioCodec(); } catch { /* ignore */ }
            hls.recoverMediaError();
          } else {
            hls.destroy();
            hlsRef.current = null;
            setPlaybackState('error');
            setLoadError('Playback error — please reload.');
            setIsPlaying(false);
          }
          return;
        }
        hls.destroy();
        hlsRef.current = null;
        setPlaybackState('error');
        setLoadError('Fatal HLS playback error');
        setIsPlaying(false);
      });
      return () => {
        hls.destroy();
        if (hlsRef.current === hls) hlsRef.current = null;
        video.pause();
      };
    }
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      video.src = playbackUrl;
      const onMeta = () => {
        if (pendingSeekRef.current != null) {
          video.currentTime = pendingSeekRef.current;
          pendingSeekRef.current = null;
        }
        setPlaybackState('playing');
        video.play().catch(() => { });
      };
      video.addEventListener('loadedmetadata', onMeta, { once: true });
      return () => {
        video.removeEventListener('loadedmetadata', onMeta);
        video.pause();
        video.removeAttribute('src');
        video.load();
      };
    }
    return undefined;
  }, [playbackUrl]);

  // Buffering indicator + stall self-heal: tunnel-transcoded chunks can take
  // seconds after a seek — show a spinner instead of a frozen frame, and nudge
  // a "playing but not advancing" video back to life.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !playbackUrl) return;
    const show = () => {
      if (bufferTimerRef.current) return;
      bufferTimerRef.current = setTimeout(() => setIsBuffering(true), 300);
    };
    const hide = () => {
      if (bufferTimerRef.current) { clearTimeout(bufferTimerRef.current); bufferTimerRef.current = null; }
      setIsBuffering(false);
    };
    const onSeeked = () => { if (v.readyState >= 3) hide(); };
    v.addEventListener('waiting', show);
    v.addEventListener('seeking', show);
    v.addEventListener('stalled', show);
    v.addEventListener('playing', hide);
    v.addEventListener('canplay', hide);
    v.addEventListener('seeked', onSeeked);
    let lastTime = -1;
    let stalls = 0;
    const iv = setInterval(() => {
      if (v.paused || v.seeking || v.readyState < 2) { lastTime = v.currentTime; stalls = 0; return; }
      if (lastTime >= 0 && Math.abs(v.currentTime - lastTime) < 0.05) {
        stalls += 1;
        if (stalls >= 2) setIsBuffering(true);
        if (stalls >= 3) {
          stalls = 0;
          try { v.currentTime = v.currentTime + 0.2; } catch { /* ignore */ }
          if (hlsRef.current) { try { hlsRef.current.startLoad(); } catch { /* ignore */ } }
          v.play().catch(() => { });
        }
      } else {
        stalls = 0;
        setIsBuffering(false);
      }
      lastTime = v.currentTime;
    }, 1000);
    return () => {
      v.removeEventListener('waiting', show);
      v.removeEventListener('seeking', show);
      v.removeEventListener('stalled', show);
      v.removeEventListener('playing', hide);
      v.removeEventListener('canplay', hide);
      v.removeEventListener('seeked', onSeeked);
      if (bufferTimerRef.current) { clearTimeout(bufferTimerRef.current); bufferTimerRef.current = null; }
      setIsBuffering(false);
      clearInterval(iv);
    };
  }, [playbackUrl]);

  /* ═══════════════════════════════════════════
   * Data fetching
   * ═══════════════════════════════════════════ */

  useEffect(() => {
    if (!selectedNode) { setCameras([]); setGroups([]); setRecConfigs(null); setLoading(false); return; }
    setLoading(true);
    // One request for every camera's recording config, so the picker can label each card with
    // what that camera is actually keeping. Deliberately NOT per-card: sixteen cards on screen
    // would otherwise mean sixteen tunnel round trips per page turn. Best-effort — a failure
    // leaves the cards unlabelled rather than blocking the picker.
    setRecConfigs(null);
    setRecConfigsFailed(false);
    // What each camera has STORED — the same one-request-for-all-cameras deal, so the cards can
    // say whether there is anything worth opening before the operator picks one.
    setStorageByCamera({});
    setStorageFailed(false);
    getRecordingStorageByCamera(selectedNode.nodeId)
      .then((res: any) => {
        const map: Record<string, any> = {};
        for (const c of res?.cameras || []) map[c.camera_id] = c;
        setStorageByCamera(map);
      })
      .catch(() => setStorageFailed(true));
    getRecordingConfigs(selectedNode.nodeId)
      .then((res: any) => {
        const map: Record<string, any> = {};
        for (const c of res?.configs || []) map[c.camera_id] = c;
        setRecConfigs(map);
      })
      .catch(() => setRecConfigsFailed(true));
    Promise.all([
      getNodeCameras(selectedNode.nodeId).then((res: any) => Array.isArray(res) ? res : res?.data || []),
      getNodeGroups(selectedNode.nodeId).then((res: any) => Array.isArray(res) ? res : res?.data || []).catch(() => []),
    ]).then(([cams, grps]) => {
      setCameras(cams);
      setGroups(grps);
      // Deep link from the live-view grid: /recordings?camera=<id> opens that
      // camera's playback directly instead of the camera picker.
      const camId = searchParams.get('camera');
      if (camId) {
        const cam = cams.find((c: any) => String(c.id || c.camera_id) === camId);
        if (cam) setSelectedCamera(cam);
      }
    }).catch(() => { setCameras([]); setGroups([]); })
      .finally(() => setLoading(false));
  }, [selectedNode]);

  useEffect(() => {
    if (!selectedNode || !selectedCamera) { setAvailableDates([]); return; }
    const camId = selectedCamera.id || selectedCamera.camera_id;
    getRecordingDates(selectedNode.nodeId, camId)
      .then((res: any) => setAvailableDates(Array.isArray(res) ? res : res?.data || res?.dates || []))
      .catch(() => setAvailableDates([]));
  }, [selectedNode, selectedCamera]);

  useEffect(() => {
    if (!selectedNode || !selectedCamera || !selectedDate) return;
    teardown();
    setPlaybackState('idle');
    setCurrentTime(null);

    setSegmentsLoading(true);
    const camId = selectedCamera.id || selectedCamera.camera_id;
    // Build local midnight / end-of-day so the backend query covers the correct IST day
    const [y, mo, dy] = selectedDate.split('-').map(Number);
    const dayStartLocal = new Date(y, mo - 1, dy, 0, 0, 0, 0);
    const dayEndLocal = new Date(y, mo - 1, dy, 23, 59, 59, 999);
    const startTime = dayStartLocal.toISOString();
    const endTime = dayEndLocal.toISOString();
    getRecordingSegments(selectedNode.nodeId, camId, startTime, endTime)
      .then((res: any) => {
        const d = res?.data || res;
        setSegments(Array.isArray(d) ? d : d?.segments || []);
      })
      .catch(() => setSegments([]))
      .finally(() => setSegmentsLoading(false));

    // Best-effort: a failure here must not affect playback, the timeline just loses its markers.
    getRecordingTriggers(selectedNode.nodeId, camId, startTime, endTime)
      .then((rows: any) => setRecTriggers(Array.isArray(rows) ? rows : []))
      .catch(() => setRecTriggers([]));
    getRecordingConfig(selectedNode.nodeId, camId)
      .then((res: any) => setRecMode(res?.config?.mode || null))
      .catch(() => setRecMode(null));
  }, [selectedNode, selectedCamera, selectedDate, teardown]);

  /* ═══════════════════════════════════════════
   * Filtered segments + refs
   * ═══════════════════════════════════════════ */

  const filteredSegments = useMemo(() => {
    if (!segments.length || !selectedDate) return [];
    const [y, mo, dy] = selectedDate.split('-').map(Number);
    const dayStart = new Date(y, mo - 1, dy, 0, 0, 0, 0); // local midnight
    const [sh, sm] = rangeStart.split(':').map(Number);
    const [eh, em] = rangeEnd.split(':').map(Number);
    const rStart = new Date(dayStart.getTime() + (sh * 60 + (sm || 0)) * 60000);
    const rEnd = new Date(dayStart.getTime() + (eh * 60 + (em || 0)) * 60000);
    return segments.filter((s: any) => {
      const st = new Date(s.start_time);
      const et = new Date(s.end_time);
      return et > rStart && st < rEnd;
    });
  }, [segments, selectedDate, rangeStart, rangeEnd]);

  useEffect(() => { filteredSegmentsRef.current = filteredSegments; }, [filteredSegments]);

  // Segments the unified playlist actually contains, with contiguous player-time
  // offsets. MUST mirror the local backend generatePlaylist (finalized +
  // OVERLAPPING [rStart, rEnd), ascending, then the LIVE TAIL — the in-progress
  // slot's fragments) so player-time ↔ wall-clock mapping lines up with the m3u8.
  const playbackSegs = useMemo(() => {
    if (!selectedDate) return [];
    const [y, mo, dy] = selectedDate.split('-').map(Number);
    const dayStart = new Date(y, mo - 1, dy, 0, 0, 0, 0);
    const [sh, sm] = rangeStart.split(':').map(Number);
    const [eh, em] = rangeEnd.split(':').map(Number);
    const rs = dayStart.getTime() + (sh * 60 + (sm || 0)) * 60000;
    const re = dayStart.getTime() + (eh * 60 + (em || 0)) * 60000;
    const rows: any[] = [];
    let acc = 0;
    segments
      .filter((s: any) => (s.status || 'finalized') === 'finalized')
      .filter((s: any) => new Date(s.end_time).getTime() > rs && new Date(s.start_time).getTime() < re)
      .sort((a: any, b: any) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
      .forEach((s: any) => {
        // playlist_duration, not duration: this table maps player-time onto
        // wall-clock, so it has to advance by the span the PLAYLIST gives each
        // segment. That is a whole number of GOP-length chunks and stops short of
        // the segment's tail, so it runs a little under the media duration —
        // using `duration` would drift a chunk per segment across the day.
        const dur = s.playlist_duration > 0 ? s.playlist_duration
          : (s.duration && s.duration > 0 ? s.duration : 300);
        rows.push({
          id: s.id,
          playerStart: acc,
          playerEnd: acc + dur,
          wallStart: new Date(s.start_time).getTime(),
          wallEnd: new Date(s.end_time).getTime(),
        });
        acc += dur;
      });

    // Live tail: the playlist appends the still-recording slot's closed fragments
    // after the finalized segments. One row per fresh in-progress segment, ending
    // "now" — the playlist is fetched at play time, so the real tail edge is
    // within a few seconds of this estimate (clicks inside the tail map by
    // wallStart offset, which is exact; only the clamped edge is approximate).
    const now = Date.now();
    segments
      .filter((s: any) => s.status === 'in-progress')
      .filter((s: any) => {
        const t = new Date(s.start_time).getTime();
        return t < re && now - t < LIVE_TAIL_MAX_AGE_MS;
      })
      .sort((a: any, b: any) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
      .forEach((s: any) => {
        const prevEnd = rows.length ? rows[rows.length - 1].wallEnd : rs;
        const wallStart = Math.max(new Date(s.start_time).getTime(), prevEnd, rs);
        const wallEnd = Math.min(now, re);
        if (wallEnd - wallStart < 1500) return;
        const dur = (wallEnd - wallStart) / 1000;
        rows.push({
          id: s.id,
          playerStart: acc,
          playerEnd: acc + dur,
          wallStart,
          wallEnd,
          live: true,
        });
        acc += dur;
      });

    return rows;
  }, [segments, selectedDate, rangeStart, rangeEnd]);

  useEffect(() => { playbackSegsRef.current = playbackSegs; }, [playbackSegs]);

  // Changing the applied range invalidates the current single-source playlist.
  useEffect(() => {
    teardown();
    setPlaybackState('idle');
    setCurrentTime(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart, rangeEnd]);

  // rAF loop for smooth playhead — maps single-source player-time → wall-clock.
  useEffect(() => {
    const tick = () => {
      const v = videoRef.current;
      if (v && !v.paused && v.readyState >= 2) {
        const pt = v.currentTime;
        const wall = playerToWall(pt);
        if (wall) setCurrentTime(wall);
        const cur = playbackSegsRef.current.find((p: any) => pt >= p.playerStart && pt < p.playerEnd);
        setActiveSegId(cur ? cur.id : null);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [playerToWall]);

  /* ═══════════════════════════════════════════
   * Event handlers
   * ═══════════════════════════════════════════ */

  const selectCamera = useCallback((cam: any) => {
    teardown();
    setPlaybackState('idle');
    setSelectedCamera(cam);
    setSegments([]);
    setCurrentTime(null);
  }, [teardown]);

  const handleCanPlay = useCallback(() => {
    const v = videoRef.current;
    if (v && pendingSeekRef.current != null) {
      const pt = pendingSeekRef.current;
      pendingSeekRef.current = null;
      try { v.currentTime = pt; } catch { /* not seekable yet */ }
    }
    setPlaybackState('playing');
    setLoadError(null);
  }, []);

  const handleEnded = useCallback(() => {
    wantPlayingRef.current = false;
    setPlaybackState('idle');
    setIsPlaying(false);
  }, []);

  /** Timeline seek — single continuous source, so just move the playhead. */
  const handleSeek = useCallback((date: Date) => {
    if (!playbackSegsRef.current.length) return;
    setCurrentTime(date);
    startPlayback(wallToPlayer(date.getTime()));
  }, [startPlayback, wallToPlayer]);

  const playSegment = useCallback((seg: any) => {
    wantPlayingRef.current = true;
    const p = playbackSegsRef.current.find((x: any) => x.id === seg.id);
    startPlayback(p ? p.playerStart : 0);
  }, [startPlayback]);

  // Start at the range START: the first playlist segment can begin before the
  // applied range (overlap semantics) — seek so playback opens at the requested time.
  const playRange = useCallback(() => {
    const segs = playbackSegsRef.current;
    if (!segs.length || !selectedDate) return;
    const [y, mo, dy] = selectedDate.split('-').map(Number);
    const dayStart = new Date(y, mo - 1, dy, 0, 0, 0, 0);
    const [sh, sm] = rangeStart.split(':').map(Number);
    wantPlayingRef.current = true;
    startPlayback(wallToPlayer(dayStart.getTime() + (sh * 60 + (sm || 0)) * 60000));
  }, [startPlayback, wallToPlayer, selectedDate, rangeStart]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v || !playbackUrl) { playRange(); return; }
    if (v.paused) {
      wantPlayingRef.current = true;
      v.play().catch(() => { });
      setIsPlaying(true);
      setPlaybackState('playing');
    } else {
      wantPlayingRef.current = false;
      v.pause();
      setIsPlaying(false);
      setPlaybackState('paused');
    }
  };

  // Step UP one notch, wrapping 16× → 0.5×.
  const cycleSpeed = () => {
    setPlaybackRate((prev) => SPEEDS[(SPEEDS.indexOf(prev) + 1) % SPEEDS.length]);
  };

  // Step DOWN one notch, wrapping 0.5× → 16×. slowerSpeed feeds the button tooltip.
  const slowerSpeed = SPEEDS[(SPEEDS.indexOf(playbackRate) - 1 + SPEEDS.length) % SPEEDS.length];
  const slowDown = () => setPlaybackRate((prev) => SPEEDS[(SPEEDS.indexOf(prev) - 1 + SPEEDS.length) % SPEEDS.length]);

  // Jump the playhead ±seconds within the single continuous source. Reuses the
  // debounced seek path (startPlayback) and nudges the timeline label immediately.
  const skip = (deltaSec: number) => {
    const v = videoRef.current;
    if (!v || !playbackUrl) return;
    const dur = v.duration || 0;
    const target = Math.max(0, dur ? Math.min(v.currentTime + deltaSec, dur) : v.currentTime + deltaSec);
    const wall = playerToWall(target);
    if (wall) setCurrentTime(wall);
    startPlayback(target);
  };

  const triggerDownload = (url: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 1000);
  };

  // Clip = an exact [start, end] the user marks by clicking two points on the
  // timeline; the backend trims boundary segments to those offsets (download-range).
  const startClip = () => { setClipMode(true); setClipSel({ start: null, end: null }); };
  const cancelClip = () => { setClipMode(false); setClipSel({ start: null, end: null }); };

  const clipMinutes = clipSel.start && clipSel.end
    ? (clipSel.end.getTime() - clipSel.start.getTime()) / 60000
    : 0;
  const clipReady = !!(clipSel.start && clipSel.end && clipMinutes > 0 && clipMinutes <= RANGE_DOWNLOAD_MAX_MIN);

  const downloadClip = () => {
    if (!selectedNode || !selectedCamera || !clipReady || !clipSel.start || !clipSel.end) return;
    setDownloading(true);
    try {
      const camId = selectedCamera.id || selectedCamera.camera_id;
      const token = localStorage.getItem('cloud_token') || '';
      const s = clipSel.start.toISOString();
      const e = clipSel.end.toISOString();
      triggerDownload(`${API_BASE}/nodes/${selectedNode.nodeId}/recordings/download-range/${camId}?start_time=${encodeURIComponent(s)}&end_time=${encodeURIComponent(e)}&token=${encodeURIComponent(token)}`);
      cancelClip();
    } finally {
      setTimeout(() => setDownloading(false), 3000);
    }
  };

  const downloadSingleSegment = (segId: string) => {
    if (!selectedNode) return;
    const token = localStorage.getItem('cloud_token') || '';
    const url = `${API_BASE}/nodes/${selectedNode.nodeId}/recordings/download/${segId}?token=${encodeURIComponent(token)}`;
    triggerDownload(url);
  };

  useEffect(() => {
    playbackRateRef.current = playbackRate;
    if (videoRef.current) videoRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  // Enforce playbackRate when video starts playing (e.g. after segment change)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const enforce = () => { v.playbackRate = playbackRateRef.current; };
    v.addEventListener('playing', enforce);
    v.addEventListener('loadeddata', enforce);
    return () => { v.removeEventListener('playing', enforce); v.removeEventListener('loadeddata', enforce); };
  }, []);

  const streamLoading = playbackState === 'loading';

  /* ═══════════════════════════════════════════
   * Range picker popover (same UX as local node): pick start AND end together
   * with custom hour/min/AM-PM columns — the AM/PM click commits the stage
   * (From advances to To, To applies the range and closes).
   * ═══════════════════════════════════════════ */
  const [rangePickerOpen, setRangePickerOpen] = useState(false);
  const rangePickerRef = useRef<HTMLDivElement>(null);
  const [pickerStage, setPickerStage] = useState<'from' | 'to'>('from');
  const stageValue = pickerStage === 'from' ? pendingStart : pendingEnd;
  const closeRangePicker = useCallback((apply: boolean) => {
    if (apply) {
      setRangeStart(pendingStart);
      setRangeEnd(pendingEnd);
    } else {
      setPendingStart(rangeStart);
      setPendingEnd(rangeEnd);
    }
    setRangePickerOpen(false);
  }, [pendingStart, pendingEnd, rangeStart, rangeEnd]);
  useEffect(() => {
    if (!rangePickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (rangePickerRef.current && !rangePickerRef.current.contains(e.target as Node)) closeRangePicker(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [rangePickerOpen, closeRangePicker]);
  const setStagePart = useCallback((part: 'h12' | 'min' | 'pm', val: number | boolean) => {
    const cur = parseHM(pickerStage === 'from' ? pendingStart : pendingEnd);
    const next = { ...cur, [part]: val };
    const hm = buildHM(next.h12, next.min, next.pm);
    if (pickerStage === 'from') setPendingStart(hm); else setPendingEnd(hm);
    return hm;
  }, [pickerStage, pendingStart, pendingEnd]);
  const pickAmPm = useCallback((pm: boolean) => {
    const hm = setStagePart('pm', pm);
    if (pickerStage === 'from') {
      setPickerStage('to');
      return;
    }
    if (pendingStart < hm) {
      setRangeStart(pendingStart);
      setRangeEnd(hm);
      setRangePickerOpen(false);
    }
  }, [setStagePart, pickerStage, pendingStart]);
  // Keep the selected hour/minute visible in their scroll columns.
  useEffect(() => {
    if (!rangePickerOpen) return;
    const box = rangePickerRef.current;
    if (!box) return;
    box.querySelectorAll('[data-col]').forEach((col) => {
      const sel = col.querySelector('[data-selected="1"]') as HTMLElement | null;
      if (sel) (col as HTMLElement).scrollTop = sel.offsetTop - (col as HTMLElement).clientHeight / 2 + sel.clientHeight / 2;
    });
  }, [rangePickerOpen, pickerStage, stageValue]);

  // Auto-scroll to the segment under the playhead
  const segmentListRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (activeSegId && segmentListRef.current) {
      const idx = filteredSegmentsRef.current.findIndex((s: any) => s.id === activeSegId);
      if (idx >= 0) {
        const items = segmentListRef.current.querySelectorAll('[data-seg-idx]');
        const target = items[idx] as HTMLElement;
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [activeSegId]);

  // Camera-selection derived state. Hooks MUST stay above the early returns
  // below — a hook after a conditional return changes the hook count between
  // renders (React #310) the moment selectedNode/selectedCamera flips.
  const filteredCameras = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cameras.filter((c: any) => {
      const matchSearch = !q || c.name?.toLowerCase().includes(q);
      const matchGroup =
        selectedGroup === 'all' ||
        c.groups?.some((g: any) => g.id === selectedGroup) ||
        c.group_id === selectedGroup;
      return matchSearch && matchGroup;
    });
  }, [cameras, selectedGroup, search]);

  const totalCameraPages = Math.max(1, Math.ceil(filteredCameras.length / CAMERAS_PER_PAGE));
  const paginatedCameras = useMemo(() => {
    const start = (cameraPage - 1) * CAMERAS_PER_PAGE;
    return filteredCameras.slice(start, start + CAMERAS_PER_PAGE);
  }, [filteredCameras, cameraPage]);

  // Reset page when the filter (group or search) changes
  useEffect(() => { setCameraPage(1); }, [selectedGroup, search]);


  /* ═══════════════════════════════════════════
   * NO NODE SELECTED
   * ═══════════════════════════════════════════ */
  if (!selectedNode) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <FilmIcon className="h-12 w-12 mb-4 opacity-40" />
        <p className="text-[15px] font-medium">No site selected</p>
        <p className="mt-1 text-[13px]">Select a site from the title bar to view recordings.</p>
      </div>
    );
  }

  /* ═══════════════════════════════════════════
   * CAMERA SELECTION VIEW
   * ═══════════════════════════════════════════ */
  if (!selectedCamera) {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-brand" />
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-foreground">Recordings</h1>
          <p className="text-[13px] text-muted-foreground">{filteredCameras.length} camera{filteredCameras.length !== 1 ? 's' : ''}</p>
        </div>

        {/* Camera search + group filter — same as the Cameras page */}
        <div className="rounded-lg border border-border bg-card shadow-card">
          <div className="flex flex-wrap items-center gap-2 p-2 sm:gap-3 sm:p-3">
            {/* Search */}
            <div className="relative w-full sm:w-64">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search cameras…"
                className="w-full rounded-lg border border-border bg-input py-2 pl-10 pr-4 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
            </div>

            {/* Group filter */}
            <div className="relative">
              <button
                onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors whitespace-nowrap ${selectedGroup !== 'all'
                  ? 'border-brand bg-brand/5 text-brand'
                  : 'border-border bg-card text-muted-foreground hover:bg-muted'
                  }`}
              >
                <FunnelIcon className="h-4 w-4" />
                <span>{selectedGroup !== 'all' ? `Group: ${findGroupById(groups, selectedGroup)?.name || ''}` : 'Filter by Group'}</span>
              </button>
              {showGroupDropdown && (
                <div className="absolute top-full left-0 mt-1 w-60 rounded-xl border border-border bg-card shadow-lg z-40 p-2 max-h-60 overflow-y-auto">
                  {selectedGroup !== 'all' && (
                    <button
                      onClick={() => { setSelectedGroup('all'); setShowGroupDropdown(false); }}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-muted-foreground hover:bg-muted"
                    >
                      <XMarkIcon className="h-4 w-4" />Clear Filter
                    </button>
                  )}
                  {renderGroupTree(buildGroupTree(groups), selectedGroup === 'all' ? undefined : selectedGroup, (g: any) => {
                    setSelectedGroup(g?.id === selectedGroup ? 'all' : g.id);
                    setShowGroupDropdown(false);
                  })}
                  {groups.length === 0 && <p className="px-2 py-3 text-[12px] text-muted-foreground text-center">No groups</p>}
                </div>
              )}
            </div>

            {/* Clear */}
            {(search || selectedGroup !== 'all') && (
              <button
                onClick={() => { setSearch(''); setSelectedGroup('all'); setShowGroupDropdown(false); }}
                className="flex items-center gap-1 rounded-lg border border-border px-2 py-2 sm:px-3 text-[13px] font-medium text-muted-foreground hover:bg-muted"
              >
                <XMarkIcon className="h-4 w-4" /><span className="hidden sm:inline">Clear</span>
              </button>
            )}
          </div>
        </div>
        {filteredCameras.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground">
            <VideoCameraIcon className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="text-[15px]">No cameras found</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {paginatedCameras.map((cam: any) => {
                const camId = cam.id || cam.camera_id;
                // One chip, two facts: the label is what this camera is configured to keep,
                // the colour is whether it is reachable. Replaces the separate status dot
                // that used to sit at the end of the row.
                const rec = recConfigsFailed
                  ? null
                  : recordingModeBadge(
                      recConfigs ? (recConfigs[camId] || IMPLICIT_RECORDING_CONFIG) : null,
                      !isCameraOnline(cam.status),
                    );
                const store = storageByCamera[camId];
                return (
                  <RecordingCameraCard
                    key={camId}
                    name={cam.name}
                    mode={rec}
                    storage={store ?? (storageFailed ? null : undefined)}
                    onClick={() => selectCamera(cam)}
                  />
                );
              })}
            </div>
            {totalCameraPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-2">
                <button onClick={() => setCameraPage(p => Math.max(1, p - 1))} disabled={cameraPage === 1}
                  className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                {(() => {
                  const pages: (number | string)[] = [];
                  if (totalCameraPages <= 7) {
                    for (let i = 1; i <= totalCameraPages; i++) pages.push(i);
                  } else {
                    pages.push(1);
                    if (cameraPage > 3) pages.push('...');
                    for (let i = Math.max(2, cameraPage - 1); i <= Math.min(totalCameraPages - 1, cameraPage + 1); i++) pages.push(i);
                    if (cameraPage < totalCameraPages - 2) pages.push('...');
                    pages.push(totalCameraPages);
                  }
                  return pages.map((p, idx) => typeof p === 'string'
                    ? <span key={`e${idx}`} className="text-[13px] text-muted-foreground px-1">…</span>
                    : <button key={p} onClick={() => setCameraPage(p)}
                      className={`min-w-[28px] h-7 rounded-lg text-[13px] font-medium transition-colors ${p === cameraPage ? 'bg-brand text-white' : 'hover:bg-muted text-muted-foreground'}`}>{p}</button>
                  );
                })()}
                <button onClick={() => setCameraPage(p => Math.min(totalCameraPages, p + 1))} disabled={cameraPage === totalCameraPages}
                  className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  /* ═══════════════════════════════════════════
   * RECORDING VIEWER (matches local node layout)
   * ═══════════════════════════════════════════ */
  // Height comes from the layout's <main>, not from a guess at the chrome above it: the app
  // header is 56px (h-14, not 64), <main> adds 12–24px of padding top AND bottom, the cloud
  // stacks notice bars and the local node can open the system-monitor panel. `100vh - 64px`
  // was therefore taller than the room available at every resolution, and the page's bottom
  // row — the transport — hung below the fold. `h-full` is exactly the space there is, and it
  // re-measures itself when a screen-share bar or an external display changes the viewport.
  return (
    <div className="flex flex-col h-full min-h-[26rem]">
      {/* Header */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 border-b border-border bg-card">
        <button onClick={() => { teardown(); setPlaybackState('idle'); setSelectedCamera(null); }}
          className="p-1.5 rounded-lg hover:bg-muted transition-colors" title="Back to cameras">
          <ArrowLeftIcon className="h-4 w-4 text-muted-foreground" />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <VideoCameraIcon className="h-5 w-5 text-brand flex-shrink-0" />
          <span className="text-[14px] sm:text-[15px] font-semibold text-foreground truncate">{selectedCamera.name}</span>
        </div>

        {/* Date picker - moves to right on desktop */}
        <div className="ml-auto flex items-center gap-2 order-3 sm:order-none cursor-pointer"
          onClick={() => { const el = document.getElementById('rec-date-picker') as HTMLInputElement; el?.showPicker?.(); el?.focus(); }}>
          <CalendarDaysIcon className="h-4 w-4 text-muted-foreground" />
          <input id="rec-date-picker" type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-input border border-border rounded-lg px-2 sm:px-3 py-1.5 text-[12px] sm:text-[13px] text-foreground cursor-pointer focus:ring-2 focus:ring-brand-ring focus:border-transparent outline-none" />
        </div>

        {/* Available dates pills - full width row on mobile */}
        {availableDates.length > 0 && (
          <div className="w-full sm:w-auto sm:ml-0 flex items-center gap-1.5 overflow-x-auto scrollbar-hide order-4 sm:order-none pb-1 sm:pb-0">
            {availableDates.map((d: string) => (
              <button key={d} onClick={() => setSelectedDate(d)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${selectedDate === d
                  ? 'bg-brand text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}>
                {fmtDate(d)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">
        {/* Left: Player + Timeline + Controls */}
        <div className="flex flex-col flex-1 min-w-0 min-h-0 overflow-y-auto">
          {/* Video Player */}
          {/* The player takes the leftover height and nothing more. It used to be a fixed
              16:9 box: on a wide screen (an ultrawide, or a shared display whose viewport is
              wide but short) width×9/16 grew past the space left, and since the row above is
              overflow-hidden the timeline and transport were simply cut off — with no
              scrollbar to reach them. The <video> letterboxes itself (object-contain), so
              filling a bounded box keeps the picture's aspect either way. */}
          <div className="relative flex-1 min-h-[9rem] bg-black">
            {playbackUrl ? (
              /* Fills the bounded box above rather than imposing a 16:9 height of its own —
                 the <video> letterboxes with object-contain, so the aspect survives. */
              <div className="relative h-full w-full bg-black">
                {/* Click the picture to play/pause — where people reach first; the transport
                    row below is for scrubbing and speed. Routed through togglePlay so the
                    page's own state (button label, playbackState, and the wantPlaying intent
                    that decides whether a segment boundary resumes) stays authoritative.
                    Deliberately not focusable: the transport's Play button is the
                    keyboard-reachable equivalent. */}
                <div
                  className="h-full w-full cursor-pointer"
                  onClick={togglePlay}
                  title={isPlaying ? 'Click to pause' : 'Click to play'}
                >
                  <video
                    ref={videoRef}
                    className="h-full w-full object-contain bg-black"
                    muted
                    onEnded={handleEnded}
                    onCanPlay={() => {
                      const v = videoRef.current;
                      // Re-apply speed, but only auto-resume across chunk/segment
                      // boundaries when the user actually wants to be playing.
                      if (v) { v.playbackRate = playbackRateRef.current; if (v.paused && wantPlayingRef.current) v.play().catch(() => { }); }
                      handleCanPlay();
                    }}
                    onPlay={() => { if (videoRef.current) videoRef.current.playbackRate = playbackRateRef.current; setIsPlaying(true); }}
                    onPause={() => setIsPlaying(false)}
                  />
                  {/* Buffering spinner — seek target chunk still transcoding on the node */}
                  {isBuffering && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none z-10">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span className="text-[12px] text-white/70">Buffering…</span>
                      </div>
                    </div>
                  )}
                  {/* Paused affordance — purely a hint. pointer-events-none so the click lands
                      on the surface handler above rather than being split between two. */}
                  {!isPlaying && !isBuffering && playbackState !== 'loading' && playbackState !== 'error' && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <div className="rounded-full bg-black/40 p-3">
                        <svg className="h-10 w-10 text-white/80" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center text-white/40">
                <div className="text-center space-y-2">
                  <VideoCameraIcon className="h-12 w-12 mx-auto opacity-30" />
                  <p className="text-[13px]">Select a time range and press play, or click a segment</p>
                </div>
              </div>
            )}
            {/* Loading overlay */}
            {streamLoading && playbackUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span className="text-[12px] text-white/70">Loading segment from local node…</span>
                </div>
              </div>
            )}
            {/* Error overlay */}
            {playbackState === 'error' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-10">
                <div className="flex flex-col items-center gap-3 max-w-xs text-center">
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-[13px] text-white/80">{loadError || 'Failed to load segment'}</span>
                  <button onClick={() => { setPlaybackState('idle'); setLoadError(null); teardown(); }}
                    className="px-3 py-1.5 rounded-lg bg-white/10 text-[12px] text-white/70 hover:bg-white/20 transition-colors">
                    Dismiss
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Timeline + Range filter */}
          <div className="shrink-0 px-3 sm:px-4 py-2 sm:py-2.5 border-b border-border bg-card space-y-2">
            <RecordingTimeline
              segments={segments}
              selectedDate={selectedDate}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              currentTime={currentTime}
              onSeek={handleSeek}
              playing={isPlaying}
              selectMode={clipMode}
              selection={clipSel}
              onSelectionChange={setClipSel}
              triggers={recTriggers}
              recordingMode={recMode}
              controls={(
            <>
              <span className="font-medium">Time range</span>
              <div className="relative" ref={rangePickerRef}>
                <button onClick={() => { setPickerStage('from'); setRangePickerOpen((o) => !o); }}
                  className={`flex items-center gap-1.5 rounded border px-2.5 py-0.5 text-[11px] text-foreground hover:bg-muted transition-colors ${rangePickerOpen ? 'border-brand ring-1 ring-brand-ring' : 'border-border'}`}>
                  <ClockIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-mono">{fmt12(rangeStart)} — {fmt12(rangeEnd)}</span>
                </button>
                {rangePickerOpen && (() => {
                  const sv = parseHM(stageValue);
                  return (
                    // Opens UPWARD (over the player): the layout containers below
                    // are overflow-hidden, so a downward popover gets clipped.
                    <div className="absolute left-0 bottom-full mb-1 z-30 w-60 rounded-lg border border-border bg-card p-3 shadow-modal space-y-2.5">
                      <div className="flex items-center gap-1.5 text-[11px]">
                        <button onClick={() => setPickerStage('from')}
                          className={`flex-1 rounded px-2 py-1 font-mono transition-colors ${pickerStage === 'from' ? 'bg-brand/10 text-brand border border-brand' : 'border border-border text-muted-foreground hover:bg-muted'}`}>
                          {fmt12(pendingStart)}
                        </button>
                        <span className="text-muted-foreground">→</span>
                        <button onClick={() => setPickerStage('to')}
                          className={`flex-1 rounded px-2 py-1 font-mono transition-colors ${pickerStage === 'to' ? 'bg-brand/10 text-brand border border-brand' : 'border border-border text-muted-foreground hover:bg-muted'}`}>
                          {fmt12(pendingEnd)}
                        </button>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        {pickerStage === 'from' ? 'Pick start time — AM/PM confirms' : 'Pick end time — AM/PM applies'}
                      </p>
                      <div className="flex gap-1.5">
                        <div data-col className="h-36 flex-1 overflow-y-auto rounded border border-border">
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                            <button key={h} data-selected={sv.h12 === h ? '1' : undefined}
                              onClick={() => setStagePart('h12', h)}
                              className={`block w-full py-1 text-center text-[12px] transition-colors ${sv.h12 === h ? 'bg-brand text-white' : 'text-foreground hover:bg-muted'}`}>
                              {h}
                            </button>
                          ))}
                        </div>
                        <div data-col className="h-36 flex-1 overflow-y-auto rounded border border-border">
                          {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                            <button key={m} data-selected={sv.min === m ? '1' : undefined}
                              onClick={() => setStagePart('min', m)}
                              className={`block w-full py-1 text-center text-[12px] transition-colors ${sv.min === m ? 'bg-brand text-white' : 'text-foreground hover:bg-muted'}`}>
                              {String(m).padStart(2, '0')}
                            </button>
                          ))}
                        </div>
                        <div className="flex flex-col gap-1.5 flex-1">
                          <button onClick={() => pickAmPm(false)}
                            className={`flex-1 rounded border text-[12px] font-medium transition-colors ${!sv.pm ? 'bg-brand text-white border-brand' : 'border-border text-foreground hover:bg-muted'}`}>
                            AM
                          </button>
                          <button onClick={() => pickAmPm(true)}
                            className={`flex-1 rounded border text-[12px] font-medium transition-colors ${sv.pm ? 'bg-brand text-white border-brand' : 'border-border text-foreground hover:bg-muted'}`}>
                            PM
                          </button>
                        </div>
                      </div>
                      {!isRangeValid && <p className="text-red-500 text-[10px]">Start must be before end</p>}
                      <div className="flex justify-end gap-2 pt-0.5">
                        <button onClick={() => closeRangePicker(false)}
                          className="rounded border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-muted transition-colors">
                          Cancel
                        </button>
                        <button onClick={() => closeRangePicker(true)} disabled={!isRangeValid}
                          className="rounded bg-brand px-3 py-1 text-[11px] font-semibold text-white hover:bg-brand-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                          OK
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <span className="text-muted-foreground">
                {filteredSegments.length} segment{filteredSegments.length !== 1 ? 's' : ''}
              </span>
            </>
              )}
            />
          </div>

          {/* Controls bar */}
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-2.5 px-3 sm:px-5 py-2 sm:py-2.5 border-b border-border bg-card">
            <PlaybackTransport
              hasStream={!!playbackUrl}
              isPlaying={isPlaying}
              playDisabled={playbackState === 'loading' || filteredSegments.length === 0}
              onPlayPause={() => (playbackUrl ? togglePlay() : playRange())}
              onSkip={skip}
              playbackRate={playbackRate}
              onSpeedUp={cycleSpeed}
              onSlowDown={slowDown}
              slowerSpeed={slowerSpeed}
            />

            {/* Clip download — pick a start/end on the timeline, then export that range */}
            {canDownload && !clipMode && (
              <button onClick={startClip} disabled={!filteredSegments.length}
                title="Mark a start and end on the timeline, then download that clip"
                className="ml-auto flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[13px] font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <ScissorsIcon className="h-4 w-4" />
                Download Clip
              </button>
            )}
            {canDownload && clipMode && (
              <div className="ml-auto flex items-center gap-2">
                <span className="text-[12px] text-muted-foreground">
                  {!clipSel.start
                    ? 'Click the timeline to set the clip start'
                    : !clipSel.end
                      ? 'Click again to set the clip end'
                      : clipMinutes > RANGE_DOWNLOAD_MAX_MIN
                        ? `Clip too long — max ${RANGE_DOWNLOAD_MAX_MIN} min`
                        : `Clip: ${fmtTime(clipSel.start)} – ${fmtTime(clipSel.end)} (${clipMinutes < 1 ? `${Math.round(clipMinutes * 60)}s` : `${clipMinutes.toFixed(1)}m`})`}
                </span>
                <button onClick={downloadClip} disabled={!clipReady || downloading}
                  className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-1.5 text-[13px] font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {downloading ? 'Preparing…' : 'Download'}
                </button>
                <button onClick={cancelClip}
                  className="rounded-lg border border-border px-2.5 py-1.5 text-[12px] font-medium text-foreground hover:bg-muted transition-colors">
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right: Segments panel */}
        <div className="w-full lg:w-80 flex-shrink-0 min-h-0 border-t lg:border-t-0 lg:border-l border-border bg-card flex flex-col max-h-[40vh] lg:max-h-none">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <div className="flex-1">
              <h3 className="text-[13px] font-semibold text-foreground">Segments</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">{rangeStart} – {rangeEnd} · {selectedDate}</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" ref={segmentListRef}>
            {segmentsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-brand" />
              </div>
            ) : filteredSegments.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <ClockIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-[13px]">No segments in this range</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredSegments.map((seg: any, i: number) => (
                  <div key={seg.id || i} data-seg-idx={i}
                    className={`flex items-center gap-2.5 px-4 py-2.5 cursor-pointer transition-colors ${activeSegId === seg.id ? 'bg-blue-100 dark:bg-blue-900/40 border-l-4 border-l-blue-500 font-semibold' : 'hover:bg-muted'}`}>
                    <div className="flex-1 min-w-0" onClick={() => playSegment(seg)}>
                      <div className="text-[12px] font-medium text-foreground">
                        {fmtTime(seg.start_time)} — {fmtTime(seg.end_time)}
                      </div>
                      <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                        <span>{seg.duration ? fmtDuration(seg.duration) : fmtDurationFromTimes(seg.start_time, seg.end_time)}</span>
                        <span className="opacity-40">·</span>
                        <span>{fmtSize(seg.file_size || seg.size)}</span>
                        {seg.codec && (
                          <span className={`px-1.5 py-px rounded text-[10px] font-semibold ${seg.codec === 'H265' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'}`}>
                            {seg.codec}
                          </span>
                        )}
                      </div>
                    </div>


                    {canDownload && <button onClick={(e) => { e.stopPropagation(); downloadSingleSegment(seg.id); }}
                      className="p-1 rounded hover:bg-muted-foreground/10 transition-colors flex-shrink-0" title="Download segment">
                      <ArrowDownTrayIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>}

                    {activeSegId === seg.id && isPlaying && (
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

